import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, shade } from './draw-utils';
import { drawSporePod } from './bullets-boss';
import { fused, rgba } from './bullets-danger';
import type { ProceduralDef } from '../types';

/*
 * FUSED enemy shots (docs/04 bomb read): real explosives that burst on a
 * timer (DSL `motion.split`) carry a fuse spark, a pulsing danger ring and a
 * blast hint that grows as the fuse runs down. Splitting bolts that are not
 * bombs (Prism's lens bolt) use a plain pulse texture instead. The bullet system picks the
 * frame from the shot's age (entities/bullet-look.ts). Children of the
 * burst use the plain family texture (`split.bullet`).
 */

const SIZE = 64;
const FUSE_CORD = 0x3a2a20;

/** Pyre's ember bomb: a charcoal shell, cracks glowing hotter, a lit fuse. */
export const bulletEnemyBomb: ProceduralDef = fused('bullet/enemy/bomb', SIZE, (ctx, c, r, p) => {
  circle(ctx, c, c, r);
  fillOutlined(ctx, COLORS.enemyBomb, 3);
  // Fuse cord toward the spark (up-right)
  ctx.beginPath();
  ctx.moveTo(c + r * 0.55, c - r * 0.55);
  ctx.lineTo(c + r * 0.95, c - r * 0.95);
  ctx.strokeStyle = css(FUSE_CORD);
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.stroke();
  // Magma cracks heat up as the fuse burns
  ctx.strokeStyle = rgba(COLORS.dangerOrange, 0.55 + 0.45 * p);
  ctx.lineWidth = 1.8 + p;
  ctx.beginPath();
  ctx.moveTo(c - r * 0.6, c - r * 0.1);
  ctx.lineTo(c - r * 0.15, c + r * 0.1);
  ctx.lineTo(c + r * 0.05, c + r * 0.55);
  ctx.moveTo(c - r * 0.15, c + r * 0.1);
  ctx.lineTo(c + r * 0.45, c - r * 0.05);
  ctx.stroke();
  // Hot rim
  circle(ctx, c, c, r * 0.92);
  ctx.strokeStyle = rgba(COLORS.dangerRed, 0.5 + 0.4 * p);
  ctx.lineWidth = 1.5;
  ctx.stroke();
}, { dx: 1.05, dy: -1.05 });

/** Broodworm egg: a spiny pod whose hatching crack is the burning fuse. */
export const bulletEnemyEgg: ProceduralDef = fused('bullet/enemy/egg', SIZE, (ctx, c, r, p) => {
  drawSporePod(ctx, c, r * 0.95, 1 + p * 0.4);
  // Hatching crack runs down from the top as it nears the burst
  ctx.beginPath();
  ctx.moveTo(c - r * 0.05, c - r * 0.95);
  ctx.lineTo(c + r * 0.18, c - r * (0.95 - 0.5 * p));
  ctx.lineTo(c - r * 0.12, c - r * (0.95 - 0.95 * p));
  ctx.strokeStyle = css(shade(COLORS.enemyFuse, 1 + 0.2 * p));
  ctx.lineWidth = 2 + p;
  ctx.lineCap = 'round';
  ctx.stroke();
}, { dx: -0.05, dy: -1.05 });
